import { createTheme } from "@mui/material/styles";

/**
 * Alight CRM ko'rinish tizimi.
 *
 * Alight yorug'lik sotadi -- LED chiroq, lenta chiroq, kabel. Panelning butun
 * ishi esa bitta savolga javob berish: qayerda nechta bor va qancha turadi.
 * Shundan kelib chiqib:
 *
 *   - yon panel to'q (asbob paneli kabi), ish maydoni yorug';
 *   - ranglar Alight'ning o'z belgisidan olingan: firuza uchburchak va
 *     yonidagi ikkita sariq chiziq. Nisbat ham saqlanadi -- firuza asosiy,
 *     sariq esa faqat bitta kichik joyda;
 *   - raqamlar matn emas, ma'lumot sifatida teriladi: mono shrift, tabular
 *     raqamlar, ustunlar bo'ylab tekis. Qoldiq va narx -- bu yerdagi eng
 *     muhim narsa, shuning uchun ular alohida ko'rinishga ega.
 */

export const tokens = {
  ink:       "#12162A",  // yon panel
  inkRaised: "#1C2140",  // yon paneldagi ko'tarilgan yuza
  inkLine:   "#2A3057",

  /*
   * Ranglar Alight'ning o'z belgisidan olingan: firuza uchburchak va uning
   * yonidagi ikkita sariq chiziq. Belgidagi nisbat shu yerda ham saqlanadi --
   * firuza asosiy rang, sariq esa faqat bitta kichik joyda (faol bo'lim
   * chetidagi chiziq) ko'rinadi.
   */
  brand:     "#038681",  // firuza -- asosiy tugmalar, havolalar
  brandSoft: "rgba(3, 134, 129, 0.14)",
  spark:     "#EDE82F",  // sariq -- faqat faol bo'lim chizig'i
};

const status = {
  new:        "#3B82F6",
  approved:   "#10B981",
  inProgress: "#F59E0B",
  completed:  "#06B6D4",
  canceled:   "#EF4444",
};

export const statusColors = status;

const shared = {
  shape: { borderRadius: 12 },
  typography: {
    fontFamily: "'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif",
    h1: { fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "2rem", letterSpacing: "-0.02em" },
    h2: { fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "1.5rem", letterSpacing: "-0.02em" },
    h3: { fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600, fontSize: "1.25rem", letterSpacing: "-0.01em" },
    h4: { fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600, fontSize: "1.0625rem" },
    h5: { fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600, fontSize: "0.9375rem" },
    h6: { fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600, fontSize: "0.875rem" },
    button: { fontWeight: 600, textTransform: "none", letterSpacing: 0 },
    body2: { fontSize: "0.875rem" },
    caption: { fontSize: "0.75rem", letterSpacing: "0.01em" },
    // Bo'limlar ustidagi kichik yorliq
    overline: {
      fontSize: "0.6875rem", fontWeight: 600, letterSpacing: "0.08em",
      textTransform: "uppercase", lineHeight: 1.6,
    },
  },
};

export const createAppTheme = (mode) => {
  const dark = mode === "dark";

  return createTheme({
    ...shared,
    palette: {
      mode,
      primary:   { main: tokens.brand, contrastText: "#FFFFFF" },
      secondary: { main: "#5B6CFF" },
      success:   { main: status.approved },
      warning:   { main: status.inProgress },
      info:      { main: status.new },
      error:     { main: status.canceled },
      background: {
        default: dark ? "#0F1220" : "#F5F6FA",
        paper:   dark ? "#171B2E" : "#FFFFFF",
      },
      text: {
        primary:   dark ? "#E8EAF2" : "#171A2B",
        secondary: dark ? "#9AA1BC" : "#6B7189",
      },
      divider: dark ? "#272C44" : "#E3E6EF",
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          /*
           * Raqamlar ustunlar bo'ylab tekis tursin.
           *
           * Mono shrift bu yerda ishlamadi: unda ming ajratgichi butun bir
           * belgi kengligini egallaydi va "1 595" ikkita alohida raqamdek
           * o'qiladi. Inter ning tabular raqamlari ham bir xil kenglikda
           * bo'ladi, lekin oraliqlar tabiiy qoladi.
           */
          ".num": {
            fontVariantNumeric: "tabular-nums",
            fontFeatureSettings: "'tnum' 1",
            letterSpacing: "-0.01em",
          },
          /* Artikul kodlari -- bu yerda mono chindan foydali: 14-1-9-0003
             kabi kodlarni belgima-belgi solishtirish oson bo'ladi. */
          ".mono": {
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: "0.8125em",
          },
          "*:focus-visible": {
            outline: `2px solid ${tokens.brand}`,
            outlineOffset: 2,
          },
          "@media (prefers-reduced-motion: reduce)": {
            "*": { animationDuration: "0.01ms !important", transitionDuration: "0.01ms !important" },
          },
          "::-webkit-scrollbar": { width: 10, height: 10 },
          "::-webkit-scrollbar-thumb": {
            background: dark ? "#2C3250" : "#D3D8E6",
            borderRadius: 8,
            border: `2px solid ${dark ? "#0F1220" : "#F5F6FA"}`,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            border: `1px solid ${dark ? "#272C44" : "#E3E6EF"}`,
          },
          elevation0: { border: "none" },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 10, paddingInline: 16 },
          sizeSmall: { paddingInline: 12 },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 8, fontWeight: 600, fontSize: "0.75rem" },
        },
      },
      MuiTextField: { defaultProps: { size: "small" } },
      MuiOutlinedInput: { styleOverrides: { root: { borderRadius: 10 } } },
      MuiTableCell: {
        styleOverrides: {
          head: {
            fontFamily: "'Inter', sans-serif",
            fontWeight: 600,
            fontSize: "0.75rem",
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: dark ? "#9AA1BC" : "#6B7189",
            borderBottom: `1px solid ${dark ? "#272C44" : "#E3E6EF"}`,
            whiteSpace: "nowrap",
          },
          body: { borderBottom: `1px solid ${dark ? "#20253C" : "#EEF0F6"}` },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: { background: tokens.ink, fontSize: "0.75rem", borderRadius: 8 },
        },
      },
      MuiAlert: { styleOverrides: { root: { borderRadius: 10 } } },

      /*
       * Accordion o'zining oq fonini olib keladi. Kategoriyalar sahifasida
       * shu sababli to'q mavzuda oq qutilar chiqib qolgan edi -- yuzani
       * mavzudan olamiz.
       */
      MuiAccordion: {
        styleOverrides: {
          root: {
            backgroundColor: "transparent",
            backgroundImage: "none",
            border: "none",
            "&:before": { display: "none" },
          },
        },
      },

      /*
       * DataGrid ham xuddi shunday: o'z ranglari MUI mavzusiga bog'lanmagan,
       * shuning uchun to'q mavzuda jadval o'rnida katta kulrang maydon
       * qolardi. Bu yerda hammasini mavzu qiymatlariga bog'laymiz, ya'ni
       * har bir jadvalda alohida tuzatish shart emas.
       */
      MuiDataGrid: {
        styleOverrides: {
          root: {
            border: `1px solid ${dark ? "#272C44" : "#E3E6EF"}`,
            borderRadius: 12,
            backgroundColor: dark ? "#171B2E" : "#FFFFFF",
            color: dark ? "#E8EAF2" : "#171A2B",
            "--DataGrid-containerBackground": dark ? "#1B2036" : "#F8F9FC",
            "--DataGrid-rowBorderColor": dark ? "#20253C" : "#EEF0F6",
          },
          columnHeaders: {
            backgroundColor: dark ? "#1B2036" : "#F8F9FC",
            borderBottom: `1px solid ${dark ? "#272C44" : "#E3E6EF"}`,
          },
          columnHeaderTitle: {
            fontWeight: 600,
            fontSize: "0.75rem",
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: dark ? "#9AA1BC" : "#6B7189",
          },
          virtualScroller: { backgroundColor: "transparent" },
          cell: { borderBottom: `1px solid ${dark ? "#20253C" : "#EEF0F6"}` },
          footerContainer: {
            backgroundColor: "transparent",
            borderTop: `1px solid ${dark ? "#272C44" : "#E3E6EF"}`,
          },
          overlay: {
            backgroundColor: "transparent",
            color: dark ? "#9AA1BC" : "#6B7189",
          },
        },
      },
    },
  });
};
